import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, X } from "lucide-react";

const INCIDENT_TYPES = {
  data_breach: "Filtració de dades",
  unauthorized_access: "Accés no autoritzat",
  data_loss: "Pèrdua de dades",
  data_corruption: "Corrupció de dades",
  accidental_disclosure: "Divulgació accidental",
  other: "Altre",
};

const RISK_LEVELS = { low: "Baix", medium: "Mitjà", high: "Alt", critical: "Crític" };

const DATA_CATS = ["Nom i cognoms", "DNI/NIF", "Email", "Dades bancàries", "Dades de salut", "Imatge (CCTV)", "Telèfon", "Adreça"];

const BLANK = {
  title: "", incident_type: "data_breach", description: "",
  discovery_date: new Date().toISOString().split("T")[0], incident_date: "",
  affected_data_categories: [], affected_subjects_count: "",
  risk_level: "medium", notified_authority: false, notification_date: "",
  notified_subjects: false, corrective_measures: "",
  status: "open", responsible_name: "", responsible_email: "",
};

export default function DataIncidentForm({ open, onClose, item, venueId, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(item ? { ...BLANK, ...item } : BLANK);
  }, [item, open]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const toggleCat = (c) => {
    const cats = form.affected_data_categories || [];
    set("affected_data_categories", cats.includes(c) ? cats.filter(x => x !== c) : [...cats, c]);
  };

  const handleSave = async () => {
    if (!form.title || !form.incident_type) return;
    setSaving(true);
    if (item?.id) {
      await hosys.entities.DataIncident.update(item.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.DataIncident.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  const needsAuthNotification = form.risk_level === "high" || form.risk_level === "critical";

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar incidència" : "Registrar incidència de dades"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>Títol *</Label>
            <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Resum breu de la incidència" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipus d'incidència *</Label>
              <Select value={form.incident_type} onValueChange={v => set("incident_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(INCIDENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nivell de risc</Label>
              <Select value={form.risk_level} onValueChange={v => set("risk_level", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RISK_LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Descripció</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} placeholder="Descriu què ha passat, com es va descobrir i l'abast..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data de descoberta *</Label>
              <Input type="date" value={form.discovery_date} onChange={e => set("discovery_date", e.target.value)} />
            </div>
            <div>
              <Label>Data de l'incident</Label>
              <Input type="date" value={form.incident_date} onChange={e => set("incident_date", e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Categories de dades afectades</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {DATA_CATS.map(c => (
                <button key={c} onClick={() => toggleCat(c)}
                  className={`text-xs rounded-full px-2.5 py-1 transition-colors border ${
                    (form.affected_data_categories || []).includes(c)
                      ? "bg-red-100 text-red-700 border-red-200 font-medium"
                      : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                  }`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Nombre aproximat d'afectats</Label>
            <Input type="number" value={form.affected_subjects_count} onChange={e => set("affected_subjects_count", Number(e.target.value))} placeholder="0" min={0} />
          </div>

          {needsAuthNotification && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              ⚠️ <strong>Atenció:</strong> Les incidències d'alt risc o crítiques s'han de notificar a l'AEPD en un termini màxim de <strong>72 hores</strong>.
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <input type="checkbox" id="notAuth" checked={form.notified_authority} onChange={e => set("notified_authority", e.target.checked)} className="accent-[#7B2D3F]" />
              <Label htmlFor="notAuth" className="cursor-pointer mb-0 text-sm">Notificat a l'AEPD</Label>
              {form.notified_authority && (
                <Input type="date" value={form.notification_date} onChange={e => set("notification_date", e.target.value)} className="ml-auto w-36 h-8 text-sm" />
              )}
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <input type="checkbox" id="notSubj" checked={form.notified_subjects} onChange={e => set("notified_subjects", e.target.checked)} className="accent-[#7B2D3F]" />
              <Label htmlFor="notSubj" className="cursor-pointer mb-0 text-sm">Afectats notificats</Label>
            </div>
          </div>

          <div>
            <Label>Mesures correctives adoptades</Label>
            <Textarea value={form.corrective_measures} onChange={e => set("corrective_measures", e.target.value)} rows={2} placeholder="Quines accions s'han pres per mitigar l'impacte..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Oberta</SelectItem>
                  <SelectItem value="investigating">En investigació</SelectItem>
                  <SelectItem value="resolved">Resolta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Responsable</Label>
              <Input value={form.responsible_name} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom del responsable" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={!form.title || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {item ? "Actualitzar" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}